





























































import gql from 'graphql-tag';
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { chain, cloneDeep, filter, find, flow, get, includes, isEqual, keyBy, map, mapKeys } from 'lodash';

import SearchQuery from '@/graphql/listing/SorListing';
import { composeColumnQuery, composeFilterQuery, composeOrderQuery } from '@/graphql/listing/SorListing.helpers';
import { mapOrder } from '@/utils';

import Listing from './Listing.vue';
import TreeView from './components/tree_view/TreeView.vue';
import { getCellPresenter } from './AttributeCellPresenterConfig';
import { ColumnSortDirection, SortBy } from './types';
import { ZenFilter } from './components/filters/types';
import ZeroState from '@/components/listing/components/ZeroState.vue';

import { userPreferencesMixin } from './components/user_preferences/UserPreferences';

export default Vue.extend({
  name: 'SorListing',
  components: {
    Listing,
    TreeView,
    ZeroState,
  },
  mixins: [userPreferencesMixin],
  props: {
    objectType: {
      // In ModelMetaSchema.modelSingular
      type: String,
      required: true,
    },
    page: {
      type: Number,
      default: 1,
    },
    oldFavorite: {
      type: Object,
      default: null,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    listingId: {
      type: String,
      default: 'sor',
    },
    fullPageLayout: {
      type: Boolean,
      default: false,
    },
    enableEditing: {
      type: Boolean,
      default: true,
    },
    showMinimal: {
      type: Boolean,
      default: false,
    },
    resizableCols: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      attributeMeta: {},

      objectTypeCount: null,
      defaultPageSize: 25,
      totalCount: null,
      defaultActiveColumns: [],
      columns: [],
      listingData: [],
    };
  },
  computed: {
    ...mapGetters(['featureFlagState']),

    state() {
      return { objectType: this.objectType, context: 'SOR' };
    },

    showZeroState(): boolean {
      if (this.loading) return false;

      return this.objectTypeCount && this.objectTypeCount.totalCount === 0;
    },
    title(): string | null {
      return get(this.objectTypesMeta, [this.objectType, 'namePlural'], null);
    },
    loading(): boolean {
      return this.$apollo.queries.persistedUserPreferences.loading || this.$apollo.queries.listingData.loading;
    },
    isEditable(): boolean {
      return this.enableEditing && get(this.objectTypesMeta, [this.objectType, 'isEditable'], true);
    },
    isRevision(): boolean {
      return this.objectType == 'Revision';
    },
    computedColumns() {
      if (!this.objectTypeMeta || !this.columnsMeta) return [];

      const columns = flow(
        columnsMeta => columnsMeta.attrs,
        attrs => filter(attrs, attr => includes(this.activeColumns, attr.name)),
        // Sort columns
        attrs => mapOrder(attrs, this.activeColumns, 'name'),
        // TODO: This should use columnsMeta directly
        attrs =>
          map(attrs, attr => ({
            key: attr.name,
            label: attr.displayName,
            customAttributeType: attr.customAttributeType,
            presenter: getCellPresenter(this.objectType, attr.name, attr.customAttributeType),
            sortable: attr.sortable,
            editable: attr.sorEditing,
            bulkEditable: attr.sorBulkEditing,
            width: get(find(this.columnSizes, ['name', attr.name]), 'size', null),
          }))
      )(this.columnsMeta);

      if (this.objectTypeMeta.hasMappings) {
        columns.unshift({
          key: 'hasMappedObjects',
          label: null,
          customAttributeType: false,
          presenter: 'TreeViewToggle',
          sortable: false,
          editable: false,
          bulkEditable: false,
          resizable: false,
        });
      }

      if (!this.showMinimal) {
        if (this.objectTypeMeta.isCommentable) {
          // TODO: Make this a selectable column
          columns.unshift({
            key: 'hasUnreadComments',
            label: 'Unread comments',
            customAttributeType: null,
            presenter: 'Comments',
            sortable: false,
            editable: false,
            bulkEditable: false,
            resizable: false,
          });
        }

        // TODO: Should be returned from GQL
        if (!this.isRevision) {
          columns.push({
            key: 'actions',
            label: 'Actions',
            customAttributeType: null,
            presenter: 'Actions',
            sortable: false,
            editable: false,
            bulkEditable: false,
            resizable: false,
          });
        }
      }

      return columns;
    },
    // TODO: handle sort by unavailable param for a given object type,
    //       i.e. if manually changing object type in URL
    defaultSort(): SortBy {
      return {
        by: this.defaultSortAttribute,
        direction: this.defaultSortDirection,
      };
    },
    defaultSortDirection(): string {
      let direction;
      switch (this.objectType) {
        case 'Revision':
          direction = ColumnSortDirection.Desc;
          break;
        default:
          direction = ColumnSortDirection.Asc;
      }
      return direction;
    },
    defaultSortAttribute(): string {
      let attributeName;
      switch (this.objectType) {
        case 'Person':
          attributeName = 'name';
          break;
        case 'SurveyEmailTemplate':
          attributeName = 'emailTitle';
          break;
        case 'Revision':
          attributeName = 'updatedAt';
          break;
        default:
          attributeName = 'title';
      }
      return attributeName;
    },
    defaultFilters(): object[] {
      // TODO implement additional handling for tree view special filters
      return [];
    },
    mandatoryFilters(): ZenFilter[] {
      // filters which will always be applied, no matter what
      let filters = [];
      if (this.objectType === 'Group') {
        filters.push({
          f: 'is_private',
          o: '=',
          v: 0,
        });
      }
      return filters;
    },
    activePageSize(): void {
      return this.config.pageSize || this.userPreferencesData.pageSize || this.defaultPageSize;
    },
    activeColumns(): string[] {
      return this.config.columns || this.userPreferencesData.columns || this.defaultActiveColumns;
    },
    columnSizes() {
      return this.userPreferencesData.columnSizes || [];
    },
    activeSort(): SortBy {
      return this.userPreferencesData.sort || this.defaultSort;
    },
    activeFilters(): ZenFilter[] {
      return this.userPreferencesData.filters || this.defaultFilters;
    },
  },
  apollo: {
    objectTypesMeta: {
      query: gql`
        query ObjectTypeMeta {
          objectTypes {
            objectType
            namePlural
            nameSingular
            isImportable
            isCreatable
            isEditable
            opensCreateCard
          }
        }
      `,
      update: data => keyBy(data.objectTypes, meta => meta.objectType),
    },
    objectTypeMeta: {
      query: gql`
        query ObjectTypeMeta($objectType: SearchType!) {
          objectType(name: $objectType) {
            isCommentable
            isExportable
            hasMappings
          }
        }
      `,
      variables() {
        return {
          objectType: this.objectType,
        };
      },
      update: data => data.objectType,
    },
    attributeMeta: {
      // TODO: TEMP remove 'backendKey' after changing:
      // - gql filters query to not use old syntax
      // - gql sort query to accept 'by' param in 'name' format
      query: gql`
        query Attributes($objectType: SearchType!, $context: AttributesContext!) {
          objectType(name: $objectType) {
            attrs(context: $context) {
              name
              backendKey # TODO: TEMP
              displayName
            }
          }
        }
      `,
      variables() {
        return {
          objectType: this.objectType,
          context: 'SOR_LISTING',
        };
      },
      update: data => keyBy(data.objectType.attrs, 'name'),
    },
    defaultColumns: {
      query: require('@/graphql/listing/DefaultColumns.gql'),
      variables() {
        return {
          type: this.objectType,
          context: 'SOR_LISTING',
        };
      },
      manual: true,
      result({ data }) {
        this.defaultActiveColumns = chain(data.defaultColumns.attrs)
          .filter(attr => attr.sorListingDefault)
          .map(attr => attr.name)
          .value();
      },
    },
    // TODO: code duplication from ColumnSettings.vue
    columnsMeta: {
      query: require('@/graphql/listing/Columns.gql'),
      variables() {
        return {
          type: this.objectType,
          context: 'SOR_LISTING',
          // TODO: move to gql
          // Necessary for reactivity even though filtering is currently done in result hook
          columns: this.activeColumns,
        };
      },
      update: data => data.objectType,
      // TODO
      // error(error) {
      //   this.error = JSON.stringify(error.message);
      // },
    },
    objectTypeCount: {
      query: gql`
        query SorListingObjects($objectType: SearchType!, $filter: JSONString!) {
          search(type: $objectType, filter: $filter) {
            totalCount
          }
        }
      `,
      variables() {
        return {
          objectType: this.objectType,
          filter: composeFilterQuery(this.mandatoryFilters, null),
        };
      },
      update(data) {
        return data.search;
      },
      error() {
        this.showAlert({
          type: 'error',
          message: 'Oops, something went wrong.',
        });
      },
    },
    listingData: {
      fetchPolicy: 'cache-and-network',
      query() {
        const isCommentable: boolean = !this.showMinimal && this.objectTypeMeta.isCommentable;
        const columns: Array<string | object> = composeColumnQuery(
          this.activeColumns,
          this.columnsMeta,
          this.attributeMeta
        );

        return SearchQuery(this.objectType, this.columnsMeta, isCommentable, [...columns]);
      },
      variables() {
        // TODO implement additional handling for tree view special filters
        let allFilters = [...this.activeFilters, ...this.mandatoryFilters];
        const filter: string = this.config.filters
          ? this.config.filters
          : composeFilterQuery(allFilters, this.attributeMeta);
        const order: object = composeOrderQuery(this.activeSort, this.attributeMeta);

        return {
          objectType: this.objectType,
          pageSize: this.activePageSize,
          currPage: this.page,
          order,
          filter,
        };
      },
      skip: function() {
        if (this.lastChangedUserPreference === 'columnSizes') {
          return true;
        }

        return (
          !this.attributeMeta ||
          !this.persistedUserPreferences ||
          this.$apollo.queries.attributeMeta.loading ||
          this.$apollo.queries.persistedUserPreferences.loading
        );
      },
      update(data) {
        this.totalCount = data.search.totalCount;

        /**
         * Adds 'mappings.' prefix to all mapped objects, which corresponds to
         * the column key for a mapped object, i.e.: 'tasks' to 'mappings.tasks'
         */
        const mappedObjectsColumns = mappedObjects =>
          mapKeys(mappedObjects, (_, objectType: string) => `mappings.${objectType}`);

        const nodeActions = function(actionPermissions) {
          let allowedActions = {};

          if (actionPermissions.canSendSurveys) {
            allowedActions.sendSurveys = { title: 'Send new questionnaire' };
          }
          if (actionPermissions.canViewSurveys) {
            allowedActions.viewSurveys = { title: 'View questionnaires' };
          }
          if (actionPermissions.surveyEmailTemplates.canClone) {
            allowedActions.cloneSurveyEmailTemplate = { title: 'Clone Template' };
          }
          if (actionPermissions.surveyEmailTemplates.canPreview) {
            allowedActions.previewSurveyEmailTemplate = { title: 'Preview Email' };
          }
          if (actionPermissions.surveyTemplates.canDuplicate) {
            allowedActions.duplicateSurveyTemplate = { title: 'Duplicate' };
          }
          if (actionPermissions.surveyTemplates.canPreview) {
            allowedActions.previewSurveyTemplate = { title: 'Preview' };
          }
          if (actionPermissions.surveyTemplates.canExport) {
            allowedActions.exportSurveyTemplate = { title: 'Export' };
          }
          if (actionPermissions.canDelete) {
            allowedActions.delete = { title: 'Delete', variant: 'danger' };
          }
          if (actionPermissions.surveyResponses.canResend) {
            allowedActions.resendSurveyResponse = { title: 'Resend Questionnaire' };
          }
          if (actionPermissions.surveyResponses.canClose) {
            allowedActions.closeSurveyResponse = { title: 'Close Questionnaire', variant: 'danger' };
          }
          if (actionPermissions.surveyResponses.canDownloadAttachments) {
            allowedActions.downloadSurveyResponse = { title: 'Download Attachments' };
          }
          if (actionPermissions.surveyResponses.canExport) {
            allowedActions.exportSurveyResponse = { title: 'Export Questionnaire Responses as CSV' };
          }

          return { actions: allowedActions };
        };

        return map(data.search.edges, edge => ({
          ...edge.node,
          // TODO: don't map __typename
          // TODO: delete the 'mappings' key
          ...mappedObjectsColumns(edge.node.mappings),
          meta: {
            // TODO: Get this info directly from GQL
            hasMappedObjects: true,
            //
            rowDetails: {
              active: false,
            },
          },
          ...nodeActions(edge.node.permissions),
        }));
      },
      error() {
        this.showAlert({
          type: 'error',
          message: 'Oops, something went wrong.',
        });
      },
    },
  },
  methods: {
    ...mapActions({
      showAlert: 'alerts/show',
    }),

    refetchListingData(): void {
      this.$apollo.queries.objectTypeCount.refetch();
      // TODO why are we refreshing data even if count returns 0?
      this.$apollo.queries.listingData.refetch();
    },

    // Events
    onRefetch(): void {
      this.refetchListingData();
    },
    onObjectCreate(): void {
      this.refetchListingData();
    },
    onUserPreferencesChange(preferences): void {
      this.setUserPreferences(preferences || this.persistedUserPreferences);
    },
    onPageChange(page: number): void {
      this.$emit('pageChange', page);
    },
    onPageSizeChange(pageSize: number): void {
      this.setUserPreference('pageSize', pageSize);
    },
    onOldFavoriteChange(data): void {
      this.$emit('oldFavoriteChange', data);
    },
    onColumnsReset(): void {
      this.setUserPreference('columns', undefined);
      this.setUserPreference('columnSizes', undefined);
    },
    onColumnsChange(columns: string[] | null): void {
      this.setUserPreference('columns', columns);
    },
    onColumnSizeChange(col, size) {
      let colSizes = cloneDeep(this.columnSizes);
      const existing = find(colSizes, ['name', col]);
      if (size === null) {
        colSizes = colSizes.filter(c => c.name !== col);
      } else if (existing) {
        existing.size = size;
      } else {
        colSizes.push({
          name: col,
          size,
        });
      }
      this.setUserPreference('columnSizes', colSizes);
    },
    onSortChange(sort: SortBy): void {
      this.setUserPreference('sort', !isEqual(sort, this.defaultSort) ? sort : undefined);
    },
    onFiltersChange(filters: ZenFilter[]): void {
      this.setUserPreference('filters', filters.length ? filters : undefined);
    },
  },
});
